import { MeasureSource } from '@core/models/measure-source';
import { DateTime } from 'luxon';

import { CalculatedType, TimestreamMetric } from '../../../models/metric.model';
import { Interval } from '../models/interval';
import { RevogridDefinition, TableHeader, TableRow } from '../models/revogrid-definition.model';
import { ConstantHelper } from '@core/helpers/constant.helper';
import { UnitHelper } from '@core/helpers/unit.helper';

export class TableHelper {
  public static MAX_COLUMNS = 4;
  public static MAX_COLUMNS_COLLAPSED = 8;

  public static generateLeftColumn(
    startDate: DateTime,
    endDate: DateTime,
    interval: Interval,
    displayFooter: boolean,
    metricType: CalculatedType = CalculatedType.STATUS
  ): Array<string> {
    const footer = displayFooter ? TableHelper.generateFooterLabels(interval, metricType) : [];
    switch (interval) {
      case Interval.THREE_MINUTES: {
        const startDate = DateTime.now().startOf('year');
        return [
          ...Array.from(Array(20), (_, i) => i).map(x => {
            const date = startDate.plus({ minutes: x * 3 });
            return date.toFormat('mm') + ' à ' + date.plus({ minutes: 3 }).toFormat('mm');
          }),
          ...footer
        ];
      }
      case Interval.HOURS: {
        let date = DateTime.now().startOf('year').set({ hour: 5 });
        const range = TableHelper.hasAmbigousHourInRange(startDate, endDate);
        const nbHours = range.hasAmbigousHour && !range.isDaylight ? 25 : 24;

        return [
          ...Array.from(Array(nbHours)).map(x => {
            if (nbHours === 24 || !range.hasAmbigousHour || date.plus({ hours: 1 }).hour !== range.ambigousDate.hour) {
              date = date.plus({ hours: 1 });
              return date.toFormat('HH') + ' à ' + date.plus({ hours: 1 }).toFormat('HH');
            } else {
              range.hasAmbigousHour = false;
              return date.plus({ hours: 1 }).toFormat('HH') + ' à ' + date.plus({ hours: 1 }).toFormat('HH');
            }
          }),
          ...footer
        ];
      }
      case Interval.DAYS: {
        return [...Array.from(Array(31), (_, i) => (i + 1).toString()), ...footer];
      }
    }
  }

  public static hasAmbigousHourInRange(
    startDate: DateTime,
    endDate: DateTime
  ): { hasAmbigousHour: boolean; ambigousDate?: DateTime; isDaylight?: boolean } {
    if (startDate.offset !== endDate.offset) {
      let date = startDate.set({ millisecond: 0, second: 0, minute: 0 });
      while (date.offset === startDate.offset) {
        date = date.plus({ hours: 1 });
      }
      return {
        hasAmbigousHour: true,
        ambigousDate: date,
        isDaylight: startDate.offset < endDate.offset
      };
    } else {
      return { hasAmbigousHour: false };
    }
  }

  public static generateFooterLabels(interval: Interval, metricType: CalculatedType): Array<string> {
    let footer = ['Moyenne', 'Minimum', 'Maximum'];
    if (metricType === CalculatedType.STATUS) {
      footer = ['Dernière', 'Minimum', 'Maximum'];
    } else if (metricType === CalculatedType.FLOW && interval !== Interval.THREE_MINUTES) {
      footer = ['Somme', 'Minimum', 'Maximum'];
    }

    return footer;
  }

  public static generateData(
    startDate: DateTime,
    endDate: DateTime,
    metrics: Array<TimestreamMetric>,
    tableStep: Interval,
    displayFooterInRows: boolean
  ): RevogridDefinition {
    const tableData: RevogridDefinition = { headers: [], rows: [], footer: null };
    let currentDate = startDate;

    if (metrics.length === 1) {
      const metricInfos: Omit<TableHeader, 'key'> = {
        nodeKey: metrics[0].key,
        category: metrics[0].category,
        metricName: metrics[0].property,
        measureType: metrics[0].measureType,
        measureSource: metrics[0].measureSource,
        simulationType: metrics[0].simulationType,
        description: metrics[0].displayName?.split('|')?.[0],
        ouvrage: metrics[0].ouvrage,
        unit: metrics[0].unit
      };
      switch (tableStep) {
        case Interval.THREE_MINUTES: {
          currentDate = currentDate.set({ millisecond: 0, second: 0, minute: 0 });
          const headers: Array<TableHeader> = [];
          const rows: Array<TableRow> = [];
          let i = 0;
          for (let column = 0; column <= 5; column++) {
            const startDateColumn = currentDate.plus({ hours: column });
            const key = startDateColumn.toISO();
            headers.push({ key, ...metricInfos });
            for (let minuteStep = 0; minuteStep < 20; minuteStep++) {
              if (!rows[minuteStep]) {
                rows[minuteStep] = {};
              }

              rows[minuteStep][key] = {
                ...metrics[0].metrics[i],
                displayValue: UnitHelper.convertAndFormat(metrics[0].metrics[i].value, metricInfos.metricName)
              };
              i++;
            }
          }
          tableData.headers = headers;
          tableData.rows = rows;
          break;
        }
        case Interval.HOURS: {
          const range = TableHelper.hasAmbigousHourInRange(startDate, endDate);
          const nbHours = range.hasAmbigousHour && !range.isDaylight ? 25 : 24;
          const endAmbigousDate = range.hasAmbigousHour ? range.ambigousDate.plus({ hours: 1 }) : null;
          currentDate = currentDate.set({ millisecond: 0, second: 0, minute: 0, hour: 6 });
          const headers: Array<TableHeader> = [];
          const rows: Array<TableRow> = [];
          let i = 0;
          for (let column = 0; column <= 5; column++) {
            let hasAmbigousHour = range.hasAmbigousHour;
            const startDateColumn = currentDate.plus({ days: column });
            const key = startDateColumn.toISO();
            headers.push({ key, ...metricInfos });
            for (let hour = 0; hour < nbHours; hour++) {
              const metric = metrics[0].metrics[i];
              const nextMetric = metrics[0].metrics[i + 1];

              if (!rows[hour]) {
                rows[hour] = {};
              }

              if (metric) {
                if (
                  hasAmbigousHour &&
                  ((range.isDaylight && DateTime.fromISO(metric.timestamp).equals(endAmbigousDate)) ||
                    (!range.isDaylight &&
                      nextMetric &&
                      DateTime.fromISO(metric.timestamp).hour === endAmbigousDate.hour &&
                      !DateTime.fromISO(nextMetric.timestamp).equals(endAmbigousDate)))
                ) {
                  rows[hour][key] = null;
                  hasAmbigousHour = false;
                } else {
                  rows[hour][key] = {
                    ...metric,
                    displayValue: UnitHelper.convertAndFormat(metric.value, metricInfos.metricName)
                  };
                  if (
                    hasAmbigousHour &&
                    nextMetric &&
                    !range.isDaylight &&
                    DateTime.fromISO(nextMetric.timestamp).equals(endAmbigousDate)
                  ) {
                    hasAmbigousHour = false;
                  }
                  i++;
                }
              }
            }
          }
          tableData.headers = headers;
          tableData.rows = rows;
          break;
        }
        case Interval.DAYS: {
          currentDate = currentDate.set({ millisecond: 0, second: 0, minute: 0, hour: 0, day: 1 });
          const headers: Array<TableHeader> = [];
          const rows: Array<TableRow> = [];
          let i = 0;
          for (let column = 0; column <= 5; column++) {
            const startDateColumn = currentDate.plus({ months: column });
            const key = startDateColumn.toISO();
            headers.push({ key, ...metricInfos });
            const maxDays = startDateColumn.daysInMonth;
            for (let day = 0; day < maxDays && i < metrics[0].metrics.length; day++) {
              if (!rows[day]) {
                rows[day] = {};
              }
              rows[day][key] = {
                ...metrics[0].metrics[i],
                displayValue: UnitHelper.convertAndFormat(metrics[0].metrics[i].value, metricInfos.metricName)
              };
              i++;
            }
          }
          tableData.headers = headers;
          tableData.rows = rows;
          break;
        }
      }

      const footer = TableHelper.calculateFooter(tableData, metrics[0].calculatedType, tableStep);
      if (displayFooterInRows) {
        tableData.rows.push(...footer);
      } else {
        tableData.footer = {
          labels: TableHelper.generateFooterLabels(tableStep, metrics[0].calculatedType),
          values: footer.reduce<Array<Array<number | string>>>((acc, cur) => {
            const values = tableData.headers.map(h => cur[h.key]?.value);
            acc.push(values);
            return acc;
          }, []),
          displayValues: footer.reduce<Array<Array<number | string>>>((acc, cur) => {
            const values = tableData.headers.map(h =>
              UnitHelper.convertAndFormat(cur[h.key]?.value, metricInfos.metricName)
            );
            acc.push(values);
            return acc;
          }, [])
        };
      }
    } else if (metrics.length > 1) {
      metrics.forEach(x =>
        tableData.headers.push({
          key: `${x.key}|${x.property}|${x.simulationType}`,
          nodeKey: x.key,
          category: x.category,
          metricName: x.property,
          measureType: x.measureType,
          measureSource: x.measureSource,
          simulationType: x.simulationType,
          description: x.displayName?.split('|')?.[0],
          ouvrage: x.ouvrage,
          unit: x.unit
        })
      );

      const tableRows = {};
      for (let column = 0; column < metrics.length; column++) {
        for (let row = 0; row < metrics[column].metrics.length; row++) {
          const rawCell = metrics[column].metrics[row];
          if (!tableRows[rawCell.timestamp]) {
            tableRows[rawCell.timestamp] = { timestamp: rawCell.timestamp };
          }
          tableRows[rawCell.timestamp][tableData.headers[column].key] = {
            ...metrics[column].metrics[row],
            displayValue: UnitHelper.convertAndFormat(
              metrics[column].metrics[row]?.value,
              tableData.headers[column].metricName
            )
          };
        }
      }
      tableData.rows = Object.keys(tableRows)
        .map(key => tableRows[key])
        .sort((a, b) => DateTime.fromISO(a.timestamp).toMillis() - DateTime.fromISO(b.timestamp).toMillis());

      // const length = metrics[0].metrics.length;
      // for (let row = 0; row < length; row++) {
      //   const tableRow: TableRow = {};
      //   for (let column = 0; column < metrics.length; column++) {
      //     tableRow[tableData.headers[column].key] = {
      //       ...metrics[column].metrics[row],
      //       displayValue: UnitHelper.convertAndFormat(
      //         metrics[column].metrics[row]?.value,
      //         tableData.headers[column].metricName
      //       )
      //     };
      //   }
      //   tableData.rows.push(tableRow);
      // }
    }

    return tableData;
  }

  public static getMaxColumns(showComparaisonHeader: boolean): number {
    return showComparaisonHeader ? TableHelper.MAX_COLUMNS : TableHelper.MAX_COLUMNS_COLLAPSED;
  }

  public static calculateFooter(
    tableData: RevogridDefinition,
    metricType: CalculatedType,
    tableStep: Interval
  ): Array<Pick<TableRow, 'value'>> {
    const calculatedRows: Array<Pick<TableRow, 'value'>> = [{ value: null }, { value: null }, { value: null }];
    tableData.headers.forEach(header => {
      let min = null as null | number;
      let max = null as null | number;
      let calc = null as null | number;
      let nbrElements = 0;
      tableData.rows.forEach(row => {
        const value = row[header.key]?.value;
        if (value !== null && value !== undefined) {
          nbrElements++;
          if (max === null || value > max) {
            max = value;
          }
          if (min === null || value < min) {
            min = value;
          }

          if (metricType === CalculatedType.STATUS) {
            calc = value;
          } else {
            calc += value;
          }
        }
      });
      if (
        (calc !== null && metricType === CalculatedType.OTHER) ||
        (calc !== null && metricType === CalculatedType.FLOW && tableStep === Interval.THREE_MINUTES)
      ) {
        calc = calc / nbrElements;
      }
      calculatedRows[0][header.key] =
        calc !== null ? { value: calc, displayValue: UnitHelper.convertAndFormat(calc, header.metricName) } : null;
      calculatedRows[1][header.key] =
        min !== null ? { value: min, displayValue: UnitHelper.convertAndFormat(min, header.metricName) } : null;
      calculatedRows[2][header.key] =
        max !== null ? { value: max, displayValue: UnitHelper.convertAndFormat(max, header.metricName) } : null;
    });

    return calculatedRows;
  }

  //#region CSV

  public static downloadFile(
    startDate: DateTime,
    leftColumn: Array<string>,
    tableData: RevogridDefinition,
    tableStep: Interval,
    isComparaison: boolean
  ): void {
    let csvContent = '\uFEFF';

    if (isComparaison) {
      csvContent += `Date;${tableData.headers.map(h => TableHelper.generateComparaisonHeaderCsv(h)).join(';')};\r\n`;
    } else {
      csvContent += `${TableHelper.getDateColumnHeaderLabel(tableStep)};${tableData.headers
        .map(h => TableHelper.generateHeaderCsv(h, tableStep))
        .join(';')};\r\n`;
    }

    leftColumn.forEach((date, i) => {
      let line = isComparaison ? `${TableHelper.getDateComparaisonColumn(date, tableStep)};` : `${date};`;
      tableData.headers.forEach(h => {
        line += `${tableData.rows[i][h.key]?.displayValue ?? ''};`;
      });
      csvContent += `${line}\r\n`;
    });

    const newBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const data = window.URL.createObjectURL(newBlob);
    const link = document.createElement('a');
    link.href = data;
    link.download = `export-${startDate.toFormat('dd-MM-yyyy-HH-mm-ss')}.csv`;
    link.click();
  }

  private static getDateColumnHeaderLabel(tableStep: Interval): string {
    if (tableStep === Interval.THREE_MINUTES) {
      return 'Minutes';
    } else if (tableStep === Interval.HOURS) {
      return 'Heures';
    } else {
      return 'Jours';
    }
  }

  private static generateHeaderCsv(header: TableHeader, tableStep: Interval): string {
    if (tableStep === Interval.THREE_MINUTES) {
      return `${DateTime.fromISO(header.key).toFormat('dd/MM/yyyy HH')}-${DateTime.fromISO(header.key)
        .plus({ hours: 1 })
        .toFormat('HH')}h`;
    } else if (tableStep === Interval.HOURS) {
      return `${DateTime.fromISO(header.key).toFormat('dd/MM/yyyy')}`;
    } else {
      return `${DateTime.fromISO(header.key).toFormat('MMMM yyyy', { locale: 'fr-FR' })}`;
    }
  }

  private static getDateComparaisonColumn(date: string, tableStep: Interval): string {
    if (tableStep === Interval.THREE_MINUTES || tableStep === Interval.HOURS) {
      return `${DateTime.fromISO(date).toFormat('dd/MM/yyyy HH:mm')}`;
    } else if (tableStep === Interval.DAYS) {
      return `${DateTime.fromISO(date).minus({ days: 1 }).toFormat('dd/MM/yyyy')}`;
    } else {
      return `${DateTime.fromISO(date).minus({ days: 1 }).toFormat('MMMM yyyy', { locale: 'fr-FR' })}`;
    }
  }

  private static generateComparaisonHeaderCsv(header: TableHeader): string {
    return `${header.measureType} - ${header.description} ${header.ouvrage ? `(${header.ouvrage})` : ''} - ${
      header.measureSource === MeasureSource.MEASURE ? 'Mesure' : 'Résultat'
    } - ${ConstantHelper.simulationToType(header.simulationType)}`;
  }

  //#endregion
}
